import React, { useState } from 'react'

import { useQueryClient } from 'react-query'

import { Link } from 'react-router-dom'

import {
  Box,
  ConfirmDialog,
  MenuOption,
  RoundedPlainTextButton,
  Typography,
} from '@percept/mui'

import { ArrowBack } from '@percept/mui/icons'

import { NavigationTabs } from 'components/NavigationTabs'

import { WebinarAdminTable } from './WebinarAdminTable'
import { ManageWebinar } from './ManageWebinar'

import { ResourceAdminTable } from './ResourceAdminTable'
import { ManageResource } from './ManageResource'

import { RESOURCE_QUERY_KEY, WEBINAR_QUERY_KEY } from '../hooks'

import { MediaAcademyResource, MediaAcademyWebinar } from '../typings'
import { AdminDialogCloseHandler } from './typings'


type MediaAcademyAdminView = 'webinars' | 'resources'


const queryKeys: Record<MediaAcademyAdminView, string> ={
  webinars: WEBINAR_QUERY_KEY,
  resources: RESOURCE_QUERY_KEY,
}

export const MediaAcademyAdmin = (): JSX.Element => {

  const [view, setView] = useState<MediaAcademyAdminView>('webinars')

  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = useState<boolean>(false)

  const [editResources, setEditResources] = useState<{
    create: boolean
    webinar: MediaAcademyWebinar | null
    resource: MediaAcademyResource | null
  }>({
    create: false,
    webinar: null,
    resource: null,
  })

  const resetEditResources = (): void => setEditResources({
    create: false,
    webinar: null,
    resource: null,
  })

  const handleDialogClose: AdminDialogCloseHandler = ({ hasUnsavedChanges }) => {
    setShowUnsavedChangesDialog(hasUnsavedChanges)
    if( !hasUnsavedChanges ){
      resetEditResources()
    }
  }

  const queryClient = useQueryClient()

  const queryKey = queryKeys[view]

  const invalidateQuery = (): void => {
    queryClient.invalidateQueries({ queryKey })
  }

  const onSuccess = (): void => {
    resetEditResources()
    invalidateQuery()
  }

  let editResourceContent = null

  if( view === 'webinars' ){
    editResourceContent = (
      (editResources.create || editResources.webinar) ? (
        <ManageWebinar  
          webinar={editResources.webinar}
          onClose={handleDialogClose}
          onSuccess={onSuccess} />
      ) : null
    )
  }
  if( view === 'resources' ){
    editResourceContent = (
      (editResources.create || editResources.resource) ? (
        <ManageResource
          resource={editResources.resource}
          onClose={handleDialogClose}
          onSuccess={onSuccess} />
      ) : null
    )
  }

  const secondaryTitleContent = (
    <RoundedPlainTextButton
      variant='contained'
      color='secondary'
      onClick={(): void => {
        setEditResources( prev => ({ ...prev, create: true, }))
      }}>
      Add New {view === 'webinars' ? 'Webinar' : 'Resource'}
    </RoundedPlainTextButton>
  )

  return (
    <Box
      p={3}>
      <Box display='flex' justifyContent='space-between'>  
        <Typography variant='h3'>Media Academy Admin</Typography>

        <Link to='/wizard/optimisation/media-academy'>
          <RoundedPlainTextButton
            variant='contained'
            size='small'
            startIcon={<ArrowBack />}>
            Back to Media Academy
          </RoundedPlainTextButton>
        </Link>
      </Box>

      <Box mt={3} mb={4}>
        <NavigationTabs
          value={view}
          options={
            [
              { value: 'webinars', label: 'Webinars' },
              { value: 'resources', label: 'Resources' },
            ] as MenuOption<MediaAcademyAdminView>[]
          }
          onChange={(view): void => {
            setView(view)
            setEditResources({ create: false, webinar: null, resource: null })
          }} />
      </Box>

      { view === 'webinars' && (
        <WebinarAdminTable
          queryKey={queryKey}
          secondaryTitleContent={secondaryTitleContent}
          invalidateQuery={invalidateQuery}
          setEditWebinar={(webinar): void => {
            setEditResources({ create: false, webinar, resource: null })
          }} />
      )}

      { view === 'resources' && (
        <ResourceAdminTable
          queryKey={queryKey}
          secondaryTitleContent={secondaryTitleContent}
          invalidateQuery={invalidateQuery}
          setEditResource={(resource): void => {
            setEditResources({ create: false, webinar: null, resource })
          }} />
      )}

      { editResourceContent }

      { showUnsavedChangesDialog && (
        <ConfirmDialog
          title='Discard Changes?'
          confirmText='Discard'
          content={
            <Box fontSize={14}>
              Your changes will not be saved.
            </Box>
          }
          onConfirm={(): void => {
            resetEditResources()
            setShowUnsavedChangesDialog(false)
          }}
          onCancel={(): void => setShowUnsavedChangesDialog(false)} />
      )}

    </Box>
  )       
}
